import {
  CheckCircle2,
  Clock3,
  CornerDownLeft,
  LoaderCircle,
  Square,
  Terminal,
  XCircle,
} from "lucide-react";
import { t } from "../../i18n";
import { CopyButton } from "../CopyButton";
import { asRecord, stringValue } from "./ToolResultView.utils";
import { asyncCommandState } from "./AsyncCommandResult.utils";

const stateLabels: Record<string, string> = {
  queued: "Command queued",
  running: "Command running",
  completed: "Command completed",
  failed: "Command failed",
  stopped: "Command stopped",
};

export function AsyncCommandResult({
  name,
  argumentsValue,
  result,
  status,
  error,
}: {
  name: string;
  argumentsValue: unknown;
  result: unknown;
  status?: string;
  error?: string;
}) {
  const input = asRecord(argumentsValue) ?? {};
  const output = asRecord(result) ?? {};
  const state = asyncCommandState(result, status, error);
  const active = state === "running" || state === "queued";
  const failed = state === "failed";
  const commandId =
    stringValue(output.commandId) || stringValue(input.commandId);
  const command = stringValue(output.command) || stringValue(input.command);
  const cwd = stringValue(output.cwd) || stringValue(input.cwd);
  const stdout = stringValue(output.stdout);
  const stderr = stringValue(output.stderr);
  const message = error || stringValue(output.error);
  const hasOutput = Boolean(stdout || stderr);
  const exitCode = typeof output.exitCode === "number" ? output.exitCode : null;
  const inputQueued = output.inputQueued === true;
  const Icon = failed
    ? XCircle
    : state === "stopped"
      ? Square
      : state === "queued"
        ? Clock3
        : active
          ? LoaderCircle
          : CheckCircle2;

  return (
    <section
      aria-label={t("Background command")}
      className="min-w-0 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm dark:border-white/10 dark:bg-stone-900 [&_button]:min-h-11 [&_button]:focus-visible:outline-2 [&_button]:focus-visible:outline-sky-500"
    >
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`grid size-10 shrink-0 place-items-center rounded-xl ${failed ? "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300" : active ? "bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300" : "bg-stone-100 text-stone-600 dark:bg-white/10 dark:text-stone-300"}`}
            >
              <Icon
                aria-hidden="true"
                size={18}
                className={
                  state === "running"
                    ? "animate-spin motion-reduce:animate-none"
                    : ""
                }
              />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold tracking-widest text-stone-500 uppercase dark:text-stone-400">
                {t("Background command")}
              </p>
              <p
                role="status"
                className="mt-0.5 text-sm font-semibold text-stone-900 dark:text-stone-100"
              >
                {t(stateLabels[state] || "Waiting for command")}
              </p>
            </div>
          </div>
          {exitCode !== null && (
            <span
              className={`rounded-lg px-2 py-1 font-mono text-xs ${exitCode === 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300"}`}
            >
              {t("Exit code {{code}}", { code: exitCode })}
            </span>
          )}
        </div>
        {stringValue(input.explanation) && (
          <p className="text-xs leading-5 text-stone-600 dark:text-stone-300">
            {stringValue(input.explanation)}
          </p>
        )}
        {commandId && (
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 rounded-lg bg-stone-50 px-3 py-1 dark:bg-white/5">
            <div className="min-w-0 flex-1 text-xs text-stone-500 dark:text-stone-400">
              <span>{t("Command ID")}</span>
              <code className="mt-0.5 block break-all text-stone-700 dark:text-stone-200">
                {commandId}
              </code>
            </div>
            <CopyButton value={commandId} label={t("Copy ID")} />
          </div>
        )}
      </div>
      {command && (
        <div className="border-y border-stone-200 bg-stone-950 px-4 py-3 dark:border-white/10">
          <div className="flex items-start gap-2 text-stone-100">
            <Terminal
              aria-hidden="true"
              size={14}
              className="mt-1 shrink-0 text-stone-400"
            />
            <pre className="min-w-0 flex-1 font-mono text-xs leading-6 break-words whitespace-pre-wrap">
              {command}
            </pre>
            <CopyButton value={command} label={t("Copy command")} />
          </div>
          {cwd && (
            <p className="mt-2 font-mono text-xs break-all text-stone-400">
              {cwd}
            </p>
          )}
        </div>
      )}
      <div className="space-y-3 p-4">
        {message && (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-5 break-words whitespace-pre-wrap text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300"
          >
            {message}
          </p>
        )}
        {name === "agent_workspace_command_stdin" && (
          <div className="rounded-xl border border-stone-200 p-3 text-xs dark:border-white/10">
            <p className="flex items-center gap-2 font-semibold text-stone-700 dark:text-stone-200">
              <CornerDownLeft size={14} aria-hidden="true" />
              {inputQueued ? t("Input queued") : t("Command input")}
            </p>
            {stringValue(input.input) && (
              <pre className="mt-2 max-h-40 overflow-auto font-mono leading-5 break-words whitespace-pre-wrap text-stone-600 dark:text-stone-300">
                {stringValue(input.input)}
              </pre>
            )}
            {input.closeStdin === true && (
              <p className="mt-2 text-stone-500 dark:text-stone-400">
                {t("Close input stream (EOF)")}
              </p>
            )}
          </div>
        )}
        {hasOutput ? (
          <div className="overflow-hidden rounded-xl bg-stone-950">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-1 text-xs text-stone-300">
              <span>{t("Command output")}</span>
              <CopyButton
                value={[stdout, stderr].filter(Boolean).join("\n")}
                label={t("Copy output")}
              />
            </div>
            <div className="max-h-96 overflow-auto p-3 font-mono text-xs leading-6">
              {stdout && (
                <pre
                  aria-label={t("Standard output")}
                  className="break-words whitespace-pre-wrap text-stone-200"
                >
                  {stdout}
                </pre>
              )}
              {stderr && (
                <div
                  className={stdout ? "mt-3 border-t border-white/10 pt-3" : ""}
                >
                  <p className="mb-1 font-sans text-[10px] font-semibold tracking-wider text-amber-300 uppercase">
                    {t("Standard error")}
                  </p>
                  <pre className="break-words whitespace-pre-wrap text-amber-200">
                    {stderr}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ) : (
          !message &&
          !inputQueued && (
            <div className="rounded-xl border border-dashed border-stone-200 px-4 py-5 text-xs leading-5 text-stone-600 dark:border-white/15 dark:text-stone-300">
              {active
                ? name === "agent_workspace_command_await" &&
                  status === "running"
                  ? t("Waiting for the command to finish…")
                  : state === "queued"
                    ? t("Command is queued. Waiting to start…")
                    : t(
                        "Command is running in the background. No output captured yet.",
                      )
                : state === "stopped"
                  ? t("Command stopped. No output captured.")
                  : t("Command completed with no output")}
            </div>
          )
        )}
      </div>
    </section>
  );
}
